"use client"

import * as Sentry from "@sentry/nextjs"
import { useEffect, useState } from "react"

import IconAlert from "@/components/global/icons/IconAlert"
import { Button } from "@/components/ui/button"
import { withBasePath } from "@/lib/redirects"

/**
 * Detects if an error is related to chunk loading failures during deployment
 */
function isChunkLoadError(error: Error): boolean {
  const errorMessage = error.message?.toLowerCase() || ""
  const errorName = error.name?.toLowerCase() || ""

  // Detection patterns for chunk loading errors
  const chunkErrorPatterns = [
    "chunkloaderror",
    "loading chunk",
    "failed to fetch dynamically imported module",
    "error in input stream",
    "failed to fetch",
    "dynamically imported module"
  ]

  // Check error message
  const hasChunkErrorMessage = chunkErrorPatterns.some((pattern) =>
    errorMessage.includes(pattern)
  )

  // Specific case: TypeError with "Error in input stream"
  const isInputStreamError =
    errorName.includes("typeerror") &&
    errorMessage.includes("error in input stream")

  return hasChunkErrorMessage || isInputStreamError
}

/**
 * Detects if an error is specifically an "Error in input stream" TypeError
 */
function isInputStreamError(error: Error): boolean {
  const errorName = error.name?.toLowerCase() || ""
  const errorMessage = error.message?.toLowerCase() || ""

  return (
    errorName.includes("typeerror") &&
    errorMessage.includes("error in input stream")
  )
}

export default function Error({
  error,
  reset
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [countdown, setCountdown] = useState(2)
  const isChunkError = isChunkLoadError(error)

  // Check if we're in a login flow and the error is the input stream error
  const isLoginInputStreamError =
    isChunkError &&
    isInputStreamError(error) &&
    typeof window !== "undefined" &&
    (window.location.pathname === "/" ||
      window.location.pathname.includes("/minasidor") ||
      window.location.search.includes("callbackUrl"))

  useEffect(() => {
    // Log the error to Sentry with enhanced context
    Sentry.captureException(error, {
      tags: {
        error_type: isChunkError ? "chunk_loading" : "general",
        auto_reload: isChunkError ? "true" : "false",
        deployment_race_condition: isChunkError ? "true" : "false"
      },
      contexts: {
        error_details: {
          error_name: error.name,
          error_message: error.message,
          user_agent:
            typeof window !== "undefined"
              ? window.navigator.userAgent
              : "unknown"
        }
      }
    })

    // Auto-reload for chunk errors after countdown
    if (isChunkError) {
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            window.location.reload()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [error, isChunkError])

  // If this is a login input stream error, don't show the error UI - the navigation will complete
  if (isLoginInputStreamError) {
    return null
  }

  // Render different UI for chunk errors vs general errors
  if (isChunkError) {
    return (
      <div className="min-h-screen bg-purple-200 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <IconAlert
              className="w-16 h-16 mx-auto mb-6 text-purple animate-pulse"
              color="currentColor"
            />
            <h1 className="text-2xl font-bold text-black mb-4">
              Uppdaterar sidan...
            </h1>
            <p className="text-base text-black-400 leading-relaxed mb-8">
              En ny version av sidan är tillgänglig. Sidan uppdateras
              automatiskt om {countdown} sekund{countdown !== 1 ? "er" : ""}.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              onClick={() => window.location.reload()}
              variant="purple"
              size="default"
              className="w-full">
              Uppdatera nu
            </Button>
          </div>

          {process.env.NODE_ENV === "development" && (
            <details className="mt-8 text-left">
              <summary className="cursor-pointer text-sm text-black-400 hover:text-black">
                Teknisk information (utveckling)
              </summary>
              <pre className="mt-4 p-4 bg-gray-accent text-xs text-black overflow-auto rounded">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }

  // General error UI
  return (
    <div className="min-h-screen bg-red-200 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <IconAlert
            className="w-16 h-16 mx-auto mb-6 text-purple"
            color="currentColor"
          />
          <h1 className="text-2xl font-bold text-black mb-4">Något gick fel</h1>
          <p className="text-base text-black-400 leading-relaxed mb-8">
            Ett oväntat fel har inträffat. Vi ber om ursäkt för besväret.
          </p>
        </div>

        <div className="space-y-4">
          <Button
            onClick={reset}
            variant="purple"
            size="default"
            className="w-full">
            Försök igen
          </Button>

          <Button
            onClick={() => (window.location.href = withBasePath("/"))}
            variant="outline-purple"
            size="default"
            className="w-full">
            Gå till startsidan
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-sm text-black-400 hover:text-black">
              Teknisk information (utveckling)
            </summary>
            <pre className="mt-4 p-4 bg-gray-accent text-xs text-black overflow-auto rounded">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}
